import { z } from "zod";
import { safeLocalStorage } from "@helpers/storage";

const sideNavModeSchema = z.enum(["open", "mini"]);

const storedSideNavStateSchema = z.object({
    mode: sideNavModeSchema,
    isHidden: z.boolean(),
    /** The open width; saves from before resizing have none. */
    width: z.number().optional(),
    /** Modes chosen inside views that keep their own (e.g. `taskView`), by view. */
    rememberedModes: z.record(z.string(), sideNavModeSchema).optional(),
});

export type StoredSideNavState = z.infer<typeof storedSideNavStateSchema>;

const parseStoredState = (stored: string) => {
    try {
        const parsed = storedSideNavStateSchema.safeParse(JSON.parse(stored));
        return parsed.success ? parsed.data : undefined;
    } catch {
        return undefined;
    }
};

/**
 * The saved `{ mode, isHidden, width?, rememberedModes? }`. Without one, a legacy hidden flag
 * (`"true"` under `legacyHiddenStorageKey`) loads as hidden and open.
 */
export const readStoredSideNavState = ({
    storageKey,
    legacyHiddenStorageKey,
}: {
    storageKey: string;
    legacyHiddenStorageKey?: string;
}): StoredSideNavState => {
    const stored = safeLocalStorage.getItem(storageKey);
    const parsed = stored === null ? undefined : parseStoredState(stored);
    if (parsed !== undefined) {
        return parsed;
    }
    const isLegacyHidden =
        legacyHiddenStorageKey !== undefined &&
        safeLocalStorage.getItem(legacyHiddenStorageKey) === "true";
    return { mode: "open", isHidden: isLegacyHidden };
};

export const writeStoredSideNavState = ({
    storageKey,
    state,
}: {
    storageKey: string;
    state: StoredSideNavState;
}) => safeLocalStorage.setItem(storageKey, JSON.stringify(state));
